import React, { useState, useEffect } from 'react';
import { Box, Drawer, List, ListItem, ListItemIcon, ListItemText, AppBar, Toolbar, Typography, IconButton, CssBaseline, Divider, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, Button, Chip, CircularProgress, Tooltip, Popover, TextField, InputAdornment } from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BusinessIcon from '@mui/icons-material/Business';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GroupIcon from '@mui/icons-material/Group';
import PeopleIcon from '@mui/icons-material/People';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import SettingsIcon from '@mui/icons-material/Settings';
import LanguageIcon from '@mui/icons-material/Language';
import MenuIcon from '@mui/icons-material/Menu';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Badge from '@mui/material/Badge';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Diversity3 from '@mui/icons-material/Diversity3';
import SpeakerNotesIcon from "@mui/icons-material/SpeakerNotes";
import EventIcon from '@mui/icons-material/Event';
import TimelineIcon from '@mui/icons-material/Timeline';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import FeedbackIcon from '@mui/icons-material/Feedback';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import LibraryMusicIcon from '@mui/icons-material/LibraryMusic';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import GavelIcon from '@mui/icons-material/Gavel';
import { useUser } from "../context/UserContext";
import NotificationDropdown from '../components/NotificationDropdown ';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import GroupsIcon from '@mui/icons-material/Groups';
import { getDisplayRoleName } from '../utils/roleUtils';
import AdminFooter from '../components/AdminFooter';
import SupportChatWidget from '../components/SupportChatWidget';
import UniversalFormTranslator from '../components/UniversalFormTranslator';
import { isAgreementCompleted, isSuperAdmin, needsAdminAgreement } from '../utils/agreementUtils';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import TranslateIcon from '@mui/icons-material/Translate';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useThemeMode } from '../context/ThemeModeContext';
import { useLanguage } from '../context/LanguageContext';
import HelpTooltip from '../components/HelpTooltip';

const drawerWidth = 240;

const resolveOrgLogo = (url) => {
  if (!url) return 'https://glocalprayer.net/prayer/public/assets/img/gp-logo.png';
  let clean = String(url).trim();
  if (clean.includes('/public/public/')) {
    clean = clean.replace('/public/public/', '/public/');
  }
  return clean;
};

export const FEATURE_HELP = {
  '/admin/dashboard': {
    title: 'Dashboard & Analytics',
    purpose: 'Real-time overview of ministry metrics, active prayers, recent members, and urgent moderation tasks.',
    howItWorks: 'Aggregates user counts, campus stats, active prayer chains, and community trends automatically.',
    mobileEffect: 'Synchronizes network announcements, statistics, and homepage cards on mobile.'
  },
  '/admin/users': {
    title: 'User & Member Management',
    purpose: 'Oversee registered believers, pastoral leaders, intercessors, and church administrators.',
    howItWorks: 'Inspect user accounts, assign roles (Super Admin, Church Admin, Intercessor), and moderate.',
    mobileEffect: 'Controls which users can post prayers, join prayer groups, lead rooms, and receive alerts.'
  },
  '/admin/user-activity': {
    title: 'Centralized User Activity',
    purpose: 'Monitor real-time believer actions across prayers, daily devotions, and prayer closet campaigns.',
    howItWorks: 'Inspect who opened, reacted, prayed, or subscribed with search, filters, and pagination.',
    mobileEffect: 'Gives pastoral admins complete visibility into member engagement and spiritual participation.'
  },
  '/admin/prayers': {
    title: 'Prayers Hub (Public & Private)',
    purpose: 'Community prayer wall for receiving, moderating, and stewarding church prayer petitions.',
    howItWorks: 'Review member requests, categorize by spiritual theme (Healing, Family, Missions), and publish.',
    mobileEffect: 'Approved prayers immediately show up on the mobile app feed where believers tap to pray.'
  },
  '/admin/public-prayers': {
    title: 'Public Community Prayers',
    purpose: 'Moderated public prayer requests viewable by all church and network members.',
    howItWorks: 'Pastors and moderators review incoming submissions before making them visible on the feed.',
    mobileEffect: 'Displays on the public prayer wall where users tap to pray and leave encouraging amens.'
  },
  '/admin/private-prayers': {
    title: 'Confidential Pastoral Prayers',
    purpose: 'Sensitive, private prayer petitions strictly reserved for ordained pastoral intercessors.',
    howItWorks: 'Requests are protected with end-to-end pastoral confidentiality and assigned to lead prayer teams.',
    mobileEffect: 'Never shown on public feeds; only assigned intercessors receive notifications to pray.'
  },
  '/admin/prayer-categories': {
    title: 'Prayer Categories',
    purpose: 'Classify prayer points into spiritual focus areas (Revival, Healing, Missions, Youth, Deliverance).',
    howItWorks: 'Create tags that help members quickly filter petitions by theme.',
    mobileEffect: 'Powers the category filter tabs inside the mobile app prayer feed.'
  },
  '/admin/prayer-closet': {
    title: 'Prayer Closet (Special Initiatives)',
    purpose: 'Disciplined targeted prayer campaigns (21-Day fasts, 24-hr chain slot relays, revival prayer rooms).',
    howItWorks: 'Set start dates, daily devotionals, continuous slot relay blocks, and intercessor signups.',
    mobileEffect: 'Mobile users book continuous prayer slots, receive push reminders, and track day-by-day progress.'
  },
  '/admin/special-prayers': {
    title: 'Targeted Prayer Campaigns',
    purpose: 'List of active, upcoming, and past congregational prayer initiatives.',
    howItWorks: 'Track participant numbers, devotion progress, and prayer slot coverage across campaigns.',
    mobileEffect: 'Renders the featured campaign carousels on the mobile app home screen.'
  },
  '/admin/subscribers': {
    title: 'Campaign Subscribers',
    purpose: 'View and manage all intercessors committed to specific prayer campaigns and slot relays.',
    howItWorks: 'Monitor individual slot bookings, check-in attendance, and send targeted campaign updates.',
    mobileEffect: 'Enables push notifications and slot check-in reminders for subscribed members.'
  },
  '/admin/share-and-accept': {
    title: 'Share & Accept (Generosity Altar)',
    purpose: 'Christian charity and mutual aid platform where members share items, clothing, books, and benevolence.',
    howItWorks: 'Review posted donation items, moderate requests, and connect givers with receivers securely.',
    mobileEffect: 'Mobile members can list blessings to give away or submit requests for needed help.'
  },
  '/admin/ride-connect': {
    title: 'Ride Connect (Church Carpool)',
    purpose: 'Church carpool ministry connecting members who need rides to worship services with volunteer drivers.',
    howItWorks: 'Coordinate church volunteer drivers, schedule pickup routes, and ensure safe travel to services.',
    mobileEffect: 'Members without transportation request rides in the app; volunteer drivers accept and navigate.'
  },
    '/admin/worships': {
    title: 'Sunday Worship & Live Streaming',
    purpose: 'Configure live stream channels and video feeds for Sunday worship services and Bible study.',
    howItWorks: 'Enter your church YouTube channel ID or stream URL so members can watch broadcasts directly in the app.',
    mobileEffect: 'Appears in the mobile app under Worships and Message section.'
  },
  '/admin/songs': {
    title: 'Songs & Hymnal Library',
    purpose: 'Comprehensive library of worship songs, hymns, chord charts, lyrics, and regional audio tracks.',
    howItWorks: 'Upload lyrics in multiple languages, PDF sheet music, and YouTube worship performance links.',
    mobileEffect: 'Intercessors access lyrics and listen to background worship audio in the mobile prayer room.'
  },
  '/admin/events': {
    title: 'Church Events & Conferences',
    purpose: 'Calendar of worship services, prayer vigils, revival conferences, and ministry seminars.',
    howItWorks: 'Schedule dates, venue locations, registration limits, and guest minister details.',
    mobileEffect: 'Displays on the mobile church calendar with interactive RSVP and automated reminder notifications.'
  },
  '/admin/daily-devotions': {
    title: 'Daily Devotion (Bread of Life)',
    purpose: 'Daily spiritual nourishment, Scripture readings, and pastoral devotionals for every day of the year.',
    howItWorks: 'Schedule 365 daily devotionals with scripture passages, reflection thoughts, and guided prayers.',
    mobileEffect: 'Delivered as the morning "Verse of the Day" notification on all user mobile lock screens.'
  },
  '/admin/testimonies': {
    title: 'Testimonies of Faith',
    purpose: 'Celebrate answered prayers, miraculous healings, and breakthroughs to build community faith.',
    howItWorks: 'Review praise reports submitted by believers and approve them for public encouragement.',
    mobileEffect: 'Featured in the mobile community feed where members react with praise and thanksgiving.'
  },
  '/admin/feedback': {
    title: 'Feedback & Member Inquiries',
    purpose: 'Direct communication channel for user feedback, support requests, and ministry inquiries.',
    howItWorks: 'Review incoming member suggestions, bug reports, or testimonies and assign follow-up actions.',
    mobileEffect: 'Members submit inquiries and suggestions via the mobile app Settings > Feedback tab.'
  },
  '/admin/groups': {
    title: 'Prayer Groups & Fellowships',
    purpose: 'Small group fellowships, cell groups, youth ministries, and localized intercessory teams.',
    howItWorks: 'Organize church members into private prayer circles with dedicated group leaders.',
    mobileEffect: 'Members interact in private group chat and shared prayer walls on their mobile devices.'
  },
  '/admin/organizations': {
    title: 'Organizations & Campuses',
    purpose: 'Multi-campus church hierarchy and ministry network management.',
    howItWorks: 'Register church branches, assign senior pastors, customize local ministry branding, and manage staff.',
    mobileEffect: 'Allows believers to connect directly with their home church campus inside the global network.'
  },
  '/admin/settings': {
    title: 'System Settings & Controls',
    purpose: 'Administrative configuration for languages, notifications, email templates, and security.',
    howItWorks: 'Configure global defaults, manage notification delivery channels, and adjust platform rules.',
    mobileEffect: 'Determines app defaults, push notification delivery, and language support on mobile.'
  },
  '/admin/admin-agreement': {
    title: 'Pastoral Integrity Agreement',
    purpose: 'Doctrinal, ethical, and confidentiality commitment for church administrators.',
    howItWorks: 'Requires pastoral admins to confirm adherence to Christian ethics and privacy policies.',
    mobileEffect: 'Guarantees member privacy and secure handling of confidential prayer requests.'
  },
  '/admin/guide': {
    title: 'Interactive System Guide',
    purpose: 'Comprehensive visual reference manual and guided walkthroughs for all admin tools.',
    howItWorks: 'Explore annotated screenshot guides, step-by-step procedures, and operational best practices.',
    mobileEffect: 'Empowers church leaders to effectively administer their mobile prayer community.'
  },
};

const navItems = [
  { key: 'nav_dashboard', label: 'Dashboard', icon: <DashboardIcon />, path: '/admin/dashboard' },
  { key: 'nav_users', label: 'Users', icon: <PeopleIcon />, path: '/admin/users' },
  { key: 'nav_prayers', label: 'Prayers', icon: <Diversity3 />, path: '/admin/prayers', isPrayers: true },
  { key: 'nav_prayer_closet', label: 'Prayer Closet', icon: <LocalFireDepartmentIcon />, path: '/admin/prayer-closet' },
  { key: 'nav_share_and_accept', label: 'Share & Accept', icon: <VolunteerActivismIcon />, path: '/admin/share-and-accept' },
  { key: 'nav_ride_connect', label: 'Ride Connect', icon: <DriveEtaIcon />, path: '/admin/ride-connect' },
  { key: 'nav_worships', label: 'Sunday Worship', icon: <LiveTvIcon />, path: '/admin/worships' },
  { key: 'nav_songs', label: 'Songs', icon: <LibraryMusicIcon />, path: '/admin/songs' },
  { key: 'nav_events', label: 'Events', icon: <EventIcon />, path: '/admin/events' },
  { key: 'nav_daily_devotions', label: 'Daily Devotion', icon: <MenuBookIcon />, path: '/admin/daily-devotions' },
  { key: 'nav_testimonies', label: 'Testimonies', icon: <SpeakerNotesIcon />, path: '/admin/testimonies' },
  { key: 'nav_feedback', label: 'Feedbacks', icon: <FeedbackIcon />, path: '/admin/feedback' },
  { key: 'nav_groups', label: 'Groups', icon: <GroupsIcon />, path: '/admin/groups' },
  { key: 'nav_organizations', label: 'Organizations', icon: <GroupIcon />, path: '/admin/organizations' },
  { key: 'nav_settings', label: 'Settings', icon: <SettingsIcon />, path: '/admin/settings' },
  { key: 'nav_admin_agreement', label: 'Admin Agreement', icon: <GavelIcon />, path: '/admin/admin-agreement' },
  { key: 'nav_guide', label: 'Guide & Help', icon: <MenuBookIcon />, path: '/admin/guide' },
];


const AdminLayout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const { mode, toggleColorMode } = useThemeMode();
  const isLight = theme.palette.mode === 'light';
  const location = useLocation();
  const navigate = useNavigate();
  const [avatarMenuAnchor, setAvatarMenuAnchor] = useState(null);
  const isAvatarMenuOpen = Boolean(avatarMenuAnchor);
  const [openOrgDialog, setOpenOrgDialog] = useState(false);
  const [openMenus, setOpenMenus] = useState({});
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  const [isAgreementPending, setIsAgreementPending] = useState(false);
  const [aboutAnchor, setAboutAnchor] = useState(null);
  const [langMenuAnchor, setLangMenuAnchor] = useState(null);
  const { currentLang, currentLanguageObj, changeLanguage, t, supportedLanguages } = useLanguage();


  const handleAvatarClick = (event) => {
    setAvatarMenuAnchor(event.currentTarget);
  };

  const handleOrgLogoClick = () => {
    setOpenOrgDialog(true);
  };

  const handleOrgDialogClose = () => {
    setOpenOrgDialog(false);
  };
  const { user, setUser, orgViewMode, setOrgViewMode, effectiveOrgId } = useUser();
  const [approvalPendingCount, setApprovalPendingCount] = useState(0);
  const [myOrganizations, setMyOrganizations] = useState(() => {
    try {
      const cached = sessionStorage.getItem('gpn_my_organizations');
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [openSwitchOrgDialog, setOpenSwitchOrgDialog] = useState(false);
  const [switchingOrgId, setSwitchingOrgId] = useState(null);
  const [switchOrgError, setSwitchOrgError] = useState('');

  // ── Org View Mode (Super Admin impersonation) ──────────────────────────
  const [openOrgViewDialog, setOpenOrgViewDialog] = useState(false);
  const [allOrganizations, setAllOrganizations] = useState([]);
  const [orgViewSearch, setOrgViewSearch] = useState('');
  const [loadingOrgs, setLoadingOrgs] = useState(false);

  // Switched Org Mode (Active ONLY when Super Admin is viewing as a specific organization)
  const isSwitchedOrgMode = Boolean(
    user?.is_switched_from_super_admin ||
    sessionStorage.getItem('gpn_sa_token') ||
    localStorage.getItem('gpn_sa_token')
  );

  // Determine effective role for UI filtering:
  const effectiveRole = isSwitchedOrgMode ? 2 : user?.role;

  const fetchMyOrganizations = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${API_URL}my-organizations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.organizations)) {
        setMyOrganizations(data.organizations);
        sessionStorage.setItem('gpn_my_organizations', JSON.stringify(data.organizations));
      }
    } catch (err) {
      console.error('Error fetching my organizations:', err);
    }
  };

  // Fetch all organizations for org-view picker (Super Admin only)
  const fetchAllOrganizations = async () => {
    if (allOrganizations.length > 0) return; // already loaded
    setLoadingOrgs(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}organizations?page=1&page_size=200`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      const orgs = data.organizations || data.data || [];
      setAllOrganizations(orgs);
    } catch (err) {
      console.error('Error fetching organizations:', err);
    } finally {
      setLoadingOrgs(false);
    }
  };

  const handleEnterOrgView = (org) => {
    setOpenOrgViewDialog(false);
    handleSwitchOrganization(org.id);
  };

  const getOrgSwitchDestination = () => {
    const currentPath = window.location.pathname;
    const restricted = ['/admin/organizations', '/admin/languages', '/admin/roles-permissions', '/admin/settings'];
    return restricted.some((p) => currentPath.startsWith(p)) ? '/admin/dashboard' : currentPath;
  };

  const handleExitOrgView = async () => {
    try {
      const saToken = sessionStorage.getItem('gpn_sa_token') || localStorage.getItem('gpn_sa_token');
      const saUserInfo = sessionStorage.getItem('gpn_sa_userInfo') || localStorage.getItem('gpn_sa_userInfo');

      // Clear the org view markers
      sessionStorage.removeItem('gpn_org_view_mode');
      sessionStorage.removeItem('gpn_sa_token');
      sessionStorage.removeItem('gpn_sa_userInfo');
      localStorage.removeItem('gpn_org_view_mode');
      localStorage.removeItem('gpn_sa_token');
      localStorage.removeItem('gpn_sa_userInfo');
      setOrgViewMode(null);

      // Attempt to switch back to Super Admin via API (using current or SA token)
      const tokenToUse = saToken || localStorage.getItem('token');
      try {
        const res = await fetch(`${API_URL}switch-organization`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokenToUse}`
          },
          body: JSON.stringify({ org_id: 'global' })
        });
        const data = await res.json();
        if (data.success && data.token) {
          localStorage.setItem('token', data.token);
          const saUser = {
            ...(data.user || {}),
            role: 1,
            org_id: null,
            organization: 'Glocal Prayer Network',
            organization_logo: null,
            is_switched_from_super_admin: false,
          };
          localStorage.setItem('userInfo', JSON.stringify(saUser));
          if (setUser) setUser(saUser);
          window.location.href = getOrgSwitchDestination();
          return;
        }
      } catch (apiErr) {
        console.warn('API switch back error, falling back to cached SA token', apiErr);
      }

      // Fallback: restore saved SA token & userInfo directly
      if (saToken && saUserInfo) {
        localStorage.setItem('token', saToken);
        const parsed = JSON.parse(saUserInfo);
        parsed.organization = 'Glocal Prayer Network';
        parsed.organization_logo = null;
        parsed.role = 1;
        parsed.org_id = null;
        parsed.is_switched_from_super_admin = false;
        localStorage.setItem('userInfo', JSON.stringify(parsed));
        if (setUser) setUser(parsed);
      }
      window.location.href = getOrgSwitchDestination();
    } catch (err) {
      console.error('Error exiting org view:', err);
      window.location.href = getOrgSwitchDestination();
    }
  };

  useEffect(() => {
    fetchMyOrganizations();
  }, [user]);

  const handleSwitchOrganization = async (targetOrgId) => {
    try {
      // "Glocal Prayer Network (Global)" selection — treat as Exit to global Super Admin view
      if (targetOrgId === 'global' || targetOrgId === 0 || targetOrgId === '0') {
        setOpenSwitchOrgDialog(false);
        await handleExitOrgView();
        return;
      }

      setSwitchingOrgId(targetOrgId);
      setSwitchOrgError('');
      const currentToken = localStorage.getItem('token');
      const currentUserInfo = localStorage.getItem('userInfo');

      // If we are currently Super Admin, backup our SA session so Exit can always restore it
      if (isSuperAdmin(user)) {
        sessionStorage.setItem('gpn_sa_token', currentToken);
        sessionStorage.setItem('gpn_sa_userInfo', currentUserInfo);
        localStorage.setItem('gpn_sa_token', currentToken);
        localStorage.setItem('gpn_sa_userInfo', currentUserInfo);
      }

      const res = await fetch(`${API_URL}switch-organization`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`
        },
        body: JSON.stringify({ org_id: targetOrgId })
      });
      const data = await res.json();
      if (data.success) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('userInfo', JSON.stringify(data.user));
        if (setUser) setUser(data.user);

        // Set orgViewMode only for Super Admin impersonation so multi-org admins remain standard admins
        if (isSuperAdmin(user) || user?.is_switched_from_super_admin) {
          const orgModeData = {
            orgId: targetOrgId,
            orgName: data.user?.organization || 'Organization',
            orgLogo: data.user?.organization_logo || null,
          };
          sessionStorage.setItem('gpn_org_view_mode', JSON.stringify(orgModeData));
          localStorage.setItem('gpn_org_view_mode', JSON.stringify(orgModeData));
          setOrgViewMode(orgModeData);
        } else {
          sessionStorage.removeItem('gpn_org_view_mode');
          localStorage.removeItem('gpn_org_view_mode');
          setOrgViewMode(null);
        }

        setOpenSwitchOrgDialog(false);
        window.location.href = getOrgSwitchDestination();
      } else {
        setSwitchOrgError(data.message || 'Failed to switch organization');
      }
    } catch (err) {
      console.error('Error switching organization:', err);
      setSwitchOrgError('Failed to switch organization');
    } finally {
      setSwitchingOrgId(null);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const [notifications, setNotifications] = useState([]);
  const [unreadNotifyCount, setUnreadNotifyCount] = useState(0);

  const [currentVerse, setCurrentVerse] = useState("");

  // Prayer-related Bible verses
  const prayerVerses = [
    "The prayer of a righteous person is powerful and effective. - James 5:16",
    "Ask and it will be given to you; seek and you will find; knock and the door will be opened to you. - Matthew 7:7",
    "The Lord is near to all who call on him, to all who call on him in truth. - Psalm 145:18",
    "Call to me and I will answer you and tell you great and unsearchable things you do not know. - Jeremiah 33:3",
    "And pray in the Spirit on all occasions with all kinds of prayers and requests. - Ephesians 6:18",
    "Devote yourselves to prayer, being watchful and thankful. - Colossians 4:2",
    "Therefore I tell you, whatever you ask for in prayer, believe that you have received it, and it will be yours. - Mark 11:24",
    "The eyes of the Lord are on the righteous and his ears are attentive to their cry. - Psalm 34:15",
    "This is the confidence we have in approaching God: that if we ask anything according to his will, he hears us. - 1 John 5:14",
    "Pray without ceasing. - 1 Thessalonians 5:17",
    "But when you pray, go into your room, close the door and pray to your Father, who is unseen. - Matthew 6:6",
    "The Lord has heard my cry for mercy; the Lord accepts my prayer. - Psalm 6:9",
    "Evening, morning and noon I cry out in distress, and he hears my voice. - Psalm 55:17",
    "I call on you, my God, for you will answer me; turn your ear to me and hear my prayer. - Psalm 17:6",
    "Hear my prayer, Lord; let my cry for help come to you. - Psalm 102:1",
    "Therefore confess your sins to each other and pray for each other so that you may be healed. - James 5:16"
  ];


  const API_URL = process.env.REACT_APP_API_URL;

  const handleAvatarMenuClose = () => {
    setAvatarMenuAnchor(null);
  };
  const handleToggleSubMenu = (label) => {
    setOpenMenus((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const handleMenuItemClick = async (action) => {
    handleAvatarMenuClose();
    if (action === 'profile') {
      navigate('/admin/my-profile');
    } else if (action === 'change-password') {
      navigate('/admin/change-password');
    } else if (action === 'switch-org') {
      fetchMyOrganizations();
      setOpenSwitchOrgDialog(true);
    } else if (action === 'logout') {
      setOpenLogoutDialog(true);
    }
  };

  const handleLogoutConfirm = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    sessionStorage.removeItem('gpn_sa_token');
    sessionStorage.removeItem('gpn_sa_userInfo');
    sessionStorage.removeItem('gpn_org_view_mode');
    sessionStorage.removeItem('gpn_my_organizations');
    localStorage.removeItem('gpn_sa_token');
    localStorage.removeItem('gpn_sa_userInfo');
    localStorage.removeItem('gpn_org_view_mode');
    localStorage.removeItem('gpn_my_organizations');
    navigate('/login');
    setOpenLogoutDialog(false);
  };


  const roleBasedAccess = {
    5: ['Dashboard', 'Users', 'Prayers', 'Prayer Closet', 'Share & Accept', 'Ride Connect', 'Sunday Worship', 'Songs', 'Events', 'Daily Devotion', 'Testimonies', 'Feedbacks', 'Guide & Help', 'Admin Agreement'],
  };

  // Items that are hidden from Org Admins (and from Super Admin when in org-view mode)
  const restrictedForNonSuperAdmin = [
    'Languages',
    'Roles & Permissions',
    'Organizations',
    'Settings',
    'Admin Agreement',
    'Guide & Help',
  ];

  // Items only visible to Super Admin (hidden in org-view mode)
  const superAdminOnlyItems = [
    'Languages',
    'Roles & Permissions',
    'Organizations',
    'Settings',
    'Admin Agreement',
    'Guide & Help',
  ];

  const filteredNavItems = navItems.filter(item => {

    // Role-specific access (e.g. Group Admin role 5)
    if (roleBasedAccess[user?.role]) {
      const allowedLabels = roleBasedAccess[user.role];
      if (!allowedLabels.includes(item.label)) return null;
      if (item.children) {
        const filteredChildren = item.children.filter(child => allowedLabels.includes(child.label));
        return { ...item, children: filteredChildren };
      }
    }

    // Super Admin restriction for non-super-admins
    if (user?.role !== 1 && restrictedForNonSuperAdmin.includes(item.label)) {
      return false;
    }

    // Org-view mode: hide Super Admin-only items so SA sees what Org Admin sees
    if (isSwitchedOrgMode && superAdminOnlyItems.includes(item.label)) {
      return false;
    }

    return true;

  });

  useEffect(() => {
    const fetchNotications = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API_URL}notifications`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();

        if (!res.ok) {
          // setError(data.message || "Failed to load notifications");
        } else {
          setNotifications(Array.isArray(data.notifications) ? data.notifications : []); // ✅ ensure array
          setUnreadNotifyCount(data.unreadCount ?? 0); // ✅ ensure number
        }
      } catch (err) {
        console.error("Network error", err);
      }
    };

    fetchNotications();


    // Set random verse on component mount
    const randomIndex = Math.floor(Math.random() * prayerVerses.length);
    setCurrentVerse(prayerVerses[randomIndex]);

  }, []);

  useEffect(() => {
    setIsAgreementPending(needsAdminAgreement(user));

    const handleAgreementUpdate = () => {
      setIsAgreementPending(needsAdminAgreement(user));
    };

    window.addEventListener('gpn_admin_agreement_updated', handleAgreementUpdate);
    return () => {
      window.removeEventListener('gpn_admin_agreement_updated', handleAgreementUpdate);
    };
  }, [user]);

  useEffect(() => {
    const fetchApprovalCounts = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const cleanUrl = (API_URL || "http://localhost:4000/api/admin/").replace(/\/+$/, '') + '/';
        const res = await fetch(`${cleanUrl}approval-counts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const d = await res.json();
          if (d.success) {
            setApprovalPendingCount(d.totalPending || 0);
          }
        }
      } catch (e) {
        // quiet error handle
      }
    };

    fetchApprovalCounts();
    const interval = setInterval(fetchApprovalCounts, 20000);
    window.addEventListener('gpn_approval_counts_updated', fetchApprovalCounts);
    return () => {
      clearInterval(interval);
      window.removeEventListener('gpn_approval_counts_updated', fetchApprovalCounts);
    };
  }, [API_URL, location.pathname]);





  const navTextColor = isLight ? '#0F172A' : '#FFFFFF';
  const navTextSelectedColor = isLight ? '#0D9488' : '#4CA1AF';
  const navBgSelected = isLight ? 'linear-gradient(90deg, rgba(13, 148, 136, 0.16) 0%, rgba(13, 148, 136, 0.06) 100%)' : 'rgba(76, 161, 175, 0.2)';
  const navBgHover = isLight ? 'rgba(13, 148, 136, 0.08)' : 'rgba(76, 161, 175, 0.15)';
  const navIconColor = isLight ? '#1E293B' : '#FFFFFF';
  const navIconSelectedColor = isLight ? '#0D9488' : '#4CA1AF';

  const drawer = (
    <Box
      data-no-translate="true"
      translate="no"
      className="notranslate"
      sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: isLight ? '#FFFFFF' : '#2C3E50',
      color: isLight ? '#0F172A' : '#FFFFFF',
    }}>

      {/* Logo Section */}
      <Box sx={{
        p: 3,
        textAlign: 'center',
        borderBottom: isLight ? '1px solid rgba(0, 0, 0, 0.08)' : '1px solid rgba(255, 255, 255, 0.1)',
        background: isLight ? 'linear-gradient(135deg, rgba(43, 130, 144, 0.08) 0%, #FFFFFF 100%)' : 'linear-gradient(135deg, rgba(76, 161, 175, 0.1) 0%, transparent 100%)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.8 }}>
          <Box
            onClick={() => {
              if (!isSwitchedOrgMode && user?.role === 1 && (!user?.org_id || user?.org_id === 'global')) {
                window.open('https://glocalprayer.net', '_blank', 'noopener,noreferrer');
              } else {
                handleOrgLogoClick();
              }
            }}
            title={isSwitchedOrgMode ? (orgViewMode?.orgName || user?.organization) : (user?.role === 1 ? 'Visit Glocal Prayer Network Website' : 'Organization Details')}
            sx={{
              width: 52,
              height: 52,
              borderRadius: '14px',
              background: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              p: 0.5,
              border: '3px solid rgba(76, 161, 175, 0.5)',
              boxShadow: '0 0 15px rgba(76, 161, 175, 0.3)',
              '&:hover': {
                transform: 'scale(1.05)',
              },
            }}
          >
            <img
              src={resolveOrgLogo(
                isSwitchedOrgMode
                  ? (orgViewMode?.orgLogo || user?.organization_logo)
                  : (user?.role === 1 && (!user?.org_id || user?.org_id === 'global'))
                    ? null
                    : user?.organization_logo
              )}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = 'https://glocalprayer.net/prayer/public/assets/img/gp-logo.png';
              }}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              alt="Organization Logo"
            />
          </Box>
        </Box>
        <Typography sx={{
          fontWeight: 700,
          color: isLight ? '#0F172A' : '#FFFFFF',
          fontSize: '0.92rem',
          letterSpacing: '0.5px',
          lineHeight: 1.3,
        }}>
          {isSwitchedOrgMode
            ? (orgViewMode?.orgName || user?.organization || 'Organization')
            : (user?.role === 1 && (!user?.org_id || user?.org_id === 'global'))
              ? 'Glocal Prayer Network'
              : (user?.organization || 'Organization')
          }
        </Typography>

        {/* In Switched Org Mode (Super Admin viewing as specific org): Show Exit Button */}
        {isSwitchedOrgMode ? (
          <Button
            size="small"
            onClick={handleExitOrgView}
            startIcon={<ExitToAppIcon sx={{ fontSize: '16px !important' }} />}
            sx={{
              mt: 1.2,
              py: 0.45,
              px: 1.8,
              fontSize: '0.74rem',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: '20px',
              color: '#FFFFFF',
              backgroundColor: 'rgba(239, 68, 68, 0.25)',
              border: '1px solid rgba(239, 68, 68, 0.5)',
              boxShadow: '0 0 10px rgba(239, 68, 68, 0.25)',
              '&:hover': {
                backgroundColor: 'rgba(239, 68, 68, 0.45)',
                borderColor: '#ef4444',
                boxShadow: '0 0 16px rgba(239, 68, 68, 0.5)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            Exit
          </Button>
        ) : (
          (user?.role === 1 || myOrganizations.length > 1) && (
            <Button
              size="small"
              onClick={() => {
                fetchMyOrganizations();
                setOpenSwitchOrgDialog(true);
              }}
              startIcon={<SwapHorizIcon sx={{ fontSize: '16px !important' }} />}
              sx={{
                mt: 1.2,
                py: 0.5,
                px: 1.6,
                fontSize: '0.78rem',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '20px',
                color: isLight ? '#0D9488' : '#4CA1AF',
                backgroundColor: isLight ? '#F0FDFA' : 'rgba(76, 161, 175, 0.12)',
                border: isLight ? '1.5px solid #0D9488' : '1px solid rgba(76, 161, 175, 0.35)',
                boxShadow: isLight ? '0 1px 4px rgba(13, 148, 136, 0.15)' : 'none',
                '&:hover': {
                  backgroundColor: isLight ? '#CCFBF1' : 'rgba(76, 161, 175, 0.25)',
                  borderColor: isLight ? '#0F766E' : '#4CA1AF',
                  boxShadow: isLight ? '0 2px 8px rgba(13, 148, 136, 0.25)' : '0 0 10px rgba(76, 161, 175, 0.4)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              Switch Org
            </Button>
          )
        )}
      </Box>

      {/* Navigation List */}
      <List sx={{ flex: 1, overflowY: 'auto', py: 2 }}>
        {filteredNavItems.map((item) => {
          let isSelected = false;
          if (item.path === '/admin/prayers') {
            isSelected = ['/admin/prayers', '/admin/public-prayers', '/admin/private-prayers', '/admin/prayer-categories'].some(p => location.pathname.startsWith(p));
          } else if (item.path === '/admin/prayer-closet' || item.path === '/admin/special-prayers') {
            isSelected = ['/admin/prayer-closet', '/admin/special-prayers', '/admin/special-prayer-categories', '/admin/subscribers', '/admin/add-special-prayer', '/admin/edit-special-prayer'].some(p => location.pathname.startsWith(p));
          } else if (item.path === '/admin/share-and-accept') {
            isSelected = ['/admin/share-and-accept', '/admin/giver-items', '/admin/giver-item-requests', '/admin/new-item-requests', '/admin/generosity-testimonies'].some(p => location.pathname.startsWith(p));
          } else if (item.path === '/admin/ride-connect') {
            isSelected = ['/admin/ride-connect', '/admin/drivers'].some(p => location.pathname.startsWith(p));
          } else if (item.path === '/admin/worships') {
            isSelected = location.pathname.startsWith('/admin/worships') || location.pathname.startsWith('/admin/sunday-worship');
          } else if (item.path === '/admin/songs') {
            isSelected = ['/admin/songs', '/admin/add-song', '/admin/edit-song', '/admin/pdf-songs'].some(p => location.pathname.startsWith(p));
          } else if (item.path === '/admin/users') {
            isSelected = ['/admin/users', '/admin/inactive-users'].some(p => location.pathname.startsWith(p));
          } else {
            isSelected = location.pathname === item.path;
          }
          if (item.children) {
            const open = openMenus[item.label] || false;
            return (
              <React.Fragment key={item.label}>
                <ListItem
                  button
                  onClick={() => handleToggleSubMenu(item.label)}
                  sx={{
                    color: navTextColor,
                    mx: 1.5,
                    mb: 0.6,
                    borderRadius: '8px',
                    fontWeight: 600,
                    '&:hover': {
                      background: navBgHover,
                      color: navTextSelectedColor,
                      '& .MuiListItemIcon-root': {
                        color: navIconSelectedColor,
                      },
                    },
                    transition: 'all 200ms ease-in-out',
                  }}
                >
                  <ListItemIcon sx={{ color: navIconColor, minWidth: 40, '& .MuiSvgIcon-root': { fontSize: 22 } }}>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={t(item.key || item.label, item.label)}
                    primaryTypographyProps={{
                      fontSize: '0.92rem',
                      fontWeight: 600,
                      color: 'inherit',
                    }}
                  />
                  {open ? <ExpandLess sx={{ color: navIconColor }} /> : <ExpandMore sx={{ color: navIconColor }} />}
                </ListItem>
                <Collapse in={open} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children.map((subItem) => (
                      <ListItem
                        key={subItem.label}
                        button
                        onClick={() => {
                          navigate(subItem.path);
                          setMobileOpen(false);
                        }}
                        selected={location.pathname === subItem.path}
                        sx={{
                          pl: 4,
                          pr: 2,
                          py: 1,
                          mx: 1.5,
                          mb: 0.5,
                          borderRadius: '8px',
                          color: location.pathname === subItem.path ? navTextSelectedColor : (isLight ? '#334155' : '#E5E7EB'),
                          background: location.pathname === subItem.path
                            ? navBgSelected
                            : 'transparent',
                          borderLeft: location.pathname === subItem.path ? `3.5px solid ${isLight ? '#0D9488' : '#4CA1AF'}` : '3.5px solid transparent',
                          '&:hover': {
                            background: navBgHover,
                            color: navTextSelectedColor,
                            '& .MuiListItemIcon-root': {
                              color: navIconSelectedColor,
                            },
                          },
                          transition: 'all 200ms ease-in-out',
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                          <FiberManualRecordIcon sx={{ fontSize: 8 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={t(subItem.key || subItem.label, subItem.label)}
                          primaryTypographyProps={{
                            fontSize: '0.86rem',
                            fontWeight: location.pathname === subItem.path ? 700 : 500,
                            color: 'inherit',
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          } else {
            return (
              <ListItem
                button
                key={item.label}
                selected={isSelected}
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
                sx={{
                  color: isSelected ? navTextSelectedColor : navTextColor,
                  background: isSelected ? navBgSelected : 'transparent',
                  borderLeft: isSelected ? `4px solid ${isLight ? '#0D9488' : '#4CA1AF'}` : '4px solid transparent',
                  mx: 1.5,
                  mb: 0.6,
                  borderRadius: '8px',
                  '&:hover': {
                    background: navBgHover,
                    color: navTextSelectedColor,
                    '& .MuiListItemIcon-root': {
                      color: navIconSelectedColor,
                    },
                  },
                  transition: 'all 200ms ease-in-out',
                }}
              >
                <ListItemIcon sx={{ color: isSelected ? navIconSelectedColor : navIconColor, minWidth: 40, '& .MuiSvgIcon-root': { fontSize: 22 } }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={t(item.key || item.label, item.label)}
                  primaryTypographyProps={{
                    fontSize: '0.92rem',
                    fontWeight: isSelected ? 700 : 600,
                    color: 'inherit',
                  }}
                />
              </ListItem>
            );
          }
        })}
      </List>
    </Box>
  );


  return (
    <Box sx={{
      display: 'flex',
      minHeight: '100vh',
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden',
      background: isLight ? '#F4F7FB' : 'linear-gradient(135deg, #1a1f2e 0%, #2C3E50 100%)',
      color: theme.palette.text.primary,
    }}>
      <CssBaseline />
      <style>{`
        @keyframes blinkPendingBadge {
          0% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.7); }
          50% { transform: scale(1.08); opacity: 0.85; box-shadow: 0 0 0 5px rgba(220, 38, 38, 0); }
          100% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 0 rgba(220, 38, 38, 0); }
        }
      `}</style>
      {/* AppBar/Header */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          background: isLight ? 'rgba(255, 255, 255, 0.95)' : 'linear-gradient(135deg, #1e293b 0%, #2C3E50 100%)',
          backdropFilter: 'blur(10px)',
          boxShadow: isLight ? '0 2px 10px rgba(0, 0, 0, 0.04)' : '0 4px 20px rgba(0, 0, 0, 0.3), 0 0 15px rgba(76, 161, 175, 0.15)',
          borderBottom: isLight ? '1px solid rgba(0, 0, 0, 0.08)' : '1px solid rgba(76, 161, 175, 0.35)',
          color: theme.palette.text.primary,
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 54, sm: 66 }, px: { xs: 1, sm: 2.5 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: { xs: 0.8, sm: 1.5 },
              display: { md: 'none' },
              color: isLight ? '#0F172A' : '#FFFFFF',
              p: { xs: 0.8, sm: 1.2 },
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Left Brand & Application Highlights */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.8, sm: 1.5 }, mr: 1, minWidth: 0 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <Typography
                  variant="h6"
                  component="div"
                  onClick={() => window.open('https://glocalprayer.net', '_blank', 'noopener,noreferrer')}
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '0.92rem', sm: '1.05rem', md: '1.2rem' },
                    lineHeight: 1.2,
                    background: isLight
                      ? 'linear-gradient(90deg, #0F172A 0%, #2B8290 100%)'
                      : 'linear-gradient(90deg, #FFFFFF 0%, #6DD5FA 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    cursor: 'pointer',
                    letterSpacing: '-0.3px',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      filter: 'brightness(1.15)',
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Glocal Prayer Network</Box>
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>GPN</Box>
                </Typography>
                <Chip
                  label="Admin"
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    background: isLight ? 'rgba(43, 130, 144, 0.12)' : 'rgba(76, 161, 175, 0.25)',
                    color: isLight ? '#2B8290' : '#4CA1AF',
                    border: isLight ? '1px solid rgba(43, 130, 144, 0.3)' : '1px solid rgba(76, 161, 175, 0.4)',
                    display: { xs: 'none', sm: 'inline-flex' },
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mt: 0.2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: isLight ? '#1E293B' : 'rgba(255, 255, 255, 0.85)',
                    fontSize: '0.78rem',
                    fontWeight: 500,
                    display: { xs: 'none', md: 'block' },
                    letterSpacing: '0.2px',
                  }}
                >
                  Uniting the Global Body of Christ • Prayer, Devotion & Generosity
                </Typography>
                <Tooltip title="About GPN & Portal Capabilities">
                  <IconButton
                    size="small"
                    onClick={(e) => setAboutAnchor(e.currentTarget)}
                    sx={{
                      p: 0.2,
                      display: { xs: 'none', sm: 'inline-flex' },
                      color: isLight ? '#0D9488' : '#4CA1AF',
                      '&:hover': { color: isLight ? '#0F766E' : '#6DD5FA', background: isLight ? 'rgba(13, 148, 136, 0.12)' : 'rgba(76, 161, 175, 0.15)' },
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          {/* Center Verse Highlight */}
          <Box sx={{ flexGrow: 1, display: { xs: 'none', xl: 'flex' }, justifyContent: 'center', alignItems: 'center', overflow: 'hidden', mx: 2 }}>
            <Box
              sx={{
                px: 2.5,
                py: 0.6,
                borderRadius: '20px',
                background: isLight ? '#F8FAFC' : 'rgba(255, 255, 255, 0.04)',
                border: isLight ? '1.5px solid #94A3B8' : '1px solid rgba(76, 161, 175, 0.25)',
                boxShadow: isLight ? '0 1px 4px rgba(15, 23, 42, 0.08)' : 'none',
                display: 'flex',
                alignItems: 'center',
                maxWidth: '560px',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: isLight ? '#0F172A' : '#E0F2FE',
                  fontStyle: 'italic',
                  fontWeight: isLight ? 700 : 400,
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: '0.88rem',
                  lineHeight: 1.4,
                }}
              >
                "{currentVerse || "The prayer of a righteous person is powerful and effective. - James 5:16"}"
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flexGrow: { xs: 1, xl: 0 } }} />

          {/* Right Section: Alert, Guide, Notification, Avatar, Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1, md: 1.2 } }}>

            {/* Super Admin: Compliance Tracker Button | Other Admins: Agreement Pending Alert if pending */}
            {!isSwitchedOrgMode && isSuperAdmin(user) ? (
              <Tooltip title="View Admin Agreement Compliance & Tracking">
                <Button
                  size="small"
                  onClick={() => navigate('/admin/admin-agreement')}
                  startIcon={<GavelIcon sx={{ fontSize: '15px !important', color: isLight ? '#0D9488' : '#4CA1AF', mr: { xs: -0.6, sm: 0.4 } }} />}
                  sx={{
                    background: location.pathname === '/admin/admin-agreement'
                      ? (isLight ? '#CCFBF1' : 'rgba(76, 161, 175, 0.28)')
                      : (isLight ? '#F0FDFA' : 'rgba(255, 255, 255, 0.06)'),
                    border: isLight ? '1.5px solid #0D9488' : '1px solid rgba(76, 161, 175, 0.4)',
                    color: isLight ? '#0D9488' : '#E0F2FE',
                    fontWeight: 700,
                    fontSize: { xs: '0.72rem', sm: '0.82rem' },
                    textTransform: 'none',
                    borderRadius: '20px',
                    px: { xs: 1, sm: 1.8 },
                    minWidth: { xs: 34, sm: 64 },
                    py: 0.5,
                    boxShadow: isLight ? '0 1px 3px rgba(13, 148, 136, 0.15)' : 'none',
                    '&:hover': {
                      background: isLight ? '#CCFBF1' : 'rgba(76, 161, 175, 0.25)',
                      borderColor: isLight ? '#0F766E' : '#4CA1AF',
                    },
                  }}
                >
                  <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>Agreement Compliance</Box>
                </Button>
              </Tooltip>
            ) : (!isSwitchedOrgMode && isAgreementPending) ? (
              <Tooltip title="Action Required: Review and complete the Admin Agreement">
                <Button
                  size="small"
                  onClick={() => navigate('/admin/admin-agreement')}
                  startIcon={<WarningAmberIcon sx={{ color: '#FBBF24 !important', fontSize: '18px !important', mr: { xs: -0.6, sm: 0.4 } }} />}
                  sx={{
                    background: 'rgba(245, 158, 11, 0.18)',
                    border: '1px solid #F59E0B',
                    color: '#FBBF24',
                    fontWeight: 700,
                    fontSize: { xs: '0.7rem', sm: '0.76rem' },
                    textTransform: 'none',
                    borderRadius: '20px',
                    px: { xs: 1, sm: 1.5 },
                    minWidth: { xs: 34, sm: 64 },
                    py: 0.4,
                    boxShadow: '0 0 10px rgba(245, 158, 11, 0.3)',
                    animation: 'agreementPulse 2s infinite',
                    '@keyframes agreementPulse': {
                      '0%': { boxShadow: '0 0 0 0 rgba(245, 158, 11, 0.5)' },
                      '70%': { boxShadow: '0 0 0 8px rgba(245, 158, 11, 0)' },
                      '100%': { boxShadow: '0 0 0 0 rgba(245, 158, 11, 0)' },
                    },
                    '&:hover': {
                      background: 'rgba(245, 158, 11, 0.28)',
                      borderColor: '#FBBF24',
                    },
                  }}
                >
                  <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>Agreement Pending</Box>
                </Button>
              </Tooltip>
            ) : null}

            {/* Quick Guide & Help icon button — hidden on mobile xs */}
            {!isSwitchedOrgMode && (
              <Tooltip title="Admin Guide & Feature Instructions">
                <IconButton
                  onClick={() => navigate('/admin/guide')}
                  sx={{
                    display: { xs: 'none', sm: 'inline-flex' },
                    color: location.pathname === '/admin/guide' ? '#4CA1AF' : (isLight ? '#475569' : '#FFFFFF'),
                    background: location.pathname === '/admin/guide'
                      ? (isLight ? 'rgba(43, 130, 144, 0.15)' : 'rgba(76, 161, 175, 0.25)')
                      : (isLight ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.06)'),
                    border: isLight ? '1px solid rgba(0, 0, 0, 0.08)' : '1px solid rgba(255, 255, 255, 0.12)',
                    width: 36,
                    height: 36,
                    '&:hover': {
                      background: isLight ? 'rgba(43, 130, 144, 0.12)' : 'rgba(76, 161, 175, 0.25)',
                      color: isLight ? '#2B8290' : '#4CA1AF',
                      borderColor: isLight ? 'rgba(43, 130, 144, 0.3)' : 'rgba(76, 161, 175, 0.4)',
                    },
                  }}
                >
                  <MenuBookIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}

            {/* Notifications */}
            <NotificationDropdown
              notifications={notifications}
              unreadCount={unreadNotifyCount}
            />

            {/* Language Selector Dropdown */}
            <Tooltip title={t('select_language', 'Select Language')}>
              <Button
                size="small"
                onClick={(e) => setLangMenuAnchor(e.currentTarget)}
                startIcon={<TranslateIcon sx={{ fontSize: '17px !important', color: isLight ? '#0D9488' : '#4CA1AF', display: { xs: 'none', sm: 'inline-flex' } }} />}
                sx={{
                  background: isLight ? 'rgba(43, 130, 144, 0.08)' : 'rgba(255, 255, 255, 0.06)',
                  border: isLight ? '1px solid rgba(43, 130, 144, 0.25)' : '1px solid rgba(255, 255, 255, 0.12)',
                  color: isLight ? '#0F172A' : '#FFFFFF',
                  fontWeight: 700,
                  fontSize: { xs: '0.74rem', sm: '0.82rem' },
                  textTransform: 'none',
                  borderRadius: '20px',
                  px: { xs: 0.8, sm: 1.4 },
                  minWidth: { xs: 34, sm: 60 },
                  py: 0.5,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: isLight ? 'rgba(43, 130, 144, 0.15)' : 'rgba(76, 161, 175, 0.2)',
                    borderColor: isLight ? '#0D9488' : '#4CA1AF',
                  },
                }}
              >
                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span style={{ fontSize: '0.95rem' }}>{currentLanguageObj?.flag || '🌐'}</span>
                  <Box sx={{ display: { xs: 'none', md: 'inline' } }}>
                    {currentLanguageObj?.nativeName || 'English'}
                  </Box>
                  <Chip
                    label={currentLanguageObj?.shortCode || currentLang.toUpperCase()}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.62rem',
                      fontWeight: 800,
                      background: isLight ? 'rgba(13, 148, 136, 0.18)' : 'rgba(76, 161, 175, 0.3)',
                      color: isLight ? '#0D9488' : '#4CA1AF',
                      ml: 0.2,
                      display: { xs: 'none', sm: 'inline-flex' },
                    }}
                  />
                </Box>
              </Button>
            </Tooltip>

            {/* Language Selection Menu */}
            <Menu
              anchorEl={langMenuAnchor}
              open={Boolean(langMenuAnchor)}
              onClose={() => setLangMenuAnchor(null)}
              PaperProps={{
                sx: {
                  mt: 1.2,
                  minWidth: 200,
                  borderRadius: '14px',
                  background: isLight ? '#FFFFFF' : '#1e293b',
                  border: isLight ? '1px solid rgba(0, 0, 0, 0.08)' : '1px solid rgba(76, 161, 175, 0.3)',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.25)',
                },
              }}
            >
              {supportedLanguages.map((lang) => {
                const isSelected = currentLang === lang.code;
                return (
                  <MenuItem
                    key={lang.code}
                    selected={isSelected}
                    onClick={() => {
                      changeLanguage(lang.code);
                      setLangMenuAnchor(null);
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 1,
                      px: 1.8,
                      fontSize: '0.88rem',
                      fontWeight: isSelected ? 700 : 500,
                      color: isSelected ? (isLight ? '#0D9488' : '#4CA1AF') : (isLight ? '#0F172A' : '#F1F5F9'),
                      '&.Mui-selected': {
                        background: isLight ? 'rgba(13, 148, 136, 0.12)' : 'rgba(76, 161, 175, 0.2)',
                      },
                      '&:hover': {
                        background: isLight ? 'rgba(13, 148, 136, 0.08)' : 'rgba(76, 161, 175, 0.15)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                      <span style={{ fontSize: '1.1rem' }}>{lang.flag}</span>
                      <Box>
                        <Typography sx={{ fontSize: '0.86rem', fontWeight: isSelected ? 700 : 600, lineHeight: 1.2 }}>
                          {lang.nativeName}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.72rem', color: isLight ? '#64748B' : '#94A3B8' }}>
                          {lang.name}
                        </Typography>
                      </Box>
                    </Box>
                    {isSelected && <CheckCircleOutlineIcon sx={{ fontSize: 18, color: isLight ? '#0D9488' : '#4CA1AF' }} />}
                  </MenuItem>
                );
              })}
            </Menu>

            {/* Dark / Light Mode Toggle Button */}
            <Tooltip title={isLight ? "Switch to Dark Mode" : "Switch to Light Mode"}>
              <IconButton
                onClick={toggleColorMode}
                aria-label="Toggle theme mode"
                sx={{
                  color: isLight ? '#2B8290' : '#FBBF24',
                  background: isLight ? 'rgba(43, 130, 144, 0.08)' : 'rgba(255, 255, 255, 0.06)',
                  border: isLight ? '1px solid rgba(43, 130, 144, 0.25)' : '1px solid rgba(255, 255, 255, 0.12)',
                  width: { xs: 32, sm: 36 },
                  height: { xs: 32, sm: 36 },
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    background: isLight ? 'rgba(43, 130, 144, 0.16)' : 'rgba(255, 255, 255, 0.12)',
                    transform: 'rotate(15deg) scale(1.05)',
                  },
                }}
              >
                {isLight ? <DarkModeIcon sx={{ fontSize: { xs: 17, sm: 19 } }} /> : <LightModeIcon sx={{ fontSize: { xs: 17, sm: 19 } }} />}
              </IconButton>
            </Tooltip>

            {/* Avatar & Profile */}
            <Box
              onClick={handleAvatarClick}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.2,
                px: { xs: 0.5, sm: 1.2 },
                py: { xs: 0.3, sm: 0.5 },
                borderRadius: '24px',
                cursor: 'pointer',
                background: isLight ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.06)',
                border: isLight ? '1px solid rgba(0, 0, 0, 0.08)' : '1px solid rgba(255, 255, 255, 0.12)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: isLight ? 'rgba(43, 130, 144, 0.12)' : 'rgba(76, 161, 175, 0.18)',
                  borderColor: isLight ? 'rgba(43, 130, 144, 0.35)' : 'rgba(76, 161, 175, 0.4)',
                },
              }}
            >
              <Avatar
                src={user?.profile_pic || undefined}
                sx={{
                  width: { xs: 28, sm: 32 },
                  height: { xs: 28, sm: 32 },
                  background: 'linear-gradient(135deg, #4CA1AF 0%, #2C3E50 100%)',
                  border: isLight ? '2px solid rgba(43, 130, 144, 0.3)' : '2px solid rgba(255,255,255,0.2)',
                  fontWeight: 700,
                  fontSize: { xs: '0.78rem', sm: '0.85rem' },
                  color: '#FFFFFF',
                }}
              >
                {(user?.name || user?.username || 'Admin').charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'left', pr: 0.5 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: isLight ? '#0F172A' : '#FFFFFF',
                    fontSize: '0.82rem',
                    lineHeight: 1.2,
                  }}
                >
                  {user?.name || user?.username || 'Admin'}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.78rem',
                    color: isLight ? '#0F766E' : '#4CA1AF',
                    fontWeight: 700,
                  }}
                >
                  {getDisplayRoleName(user?.role)}
                </Typography>
              </Box>
            </Box>

            {/* Main Application Logo on Right Side with redirect to glocalprayer.net - hidden on mobile xs */}
            <Tooltip title="Visit GPN Website (glocalprayer.net)">
              <Box
                component="a"
                href="https://glocalprayer.net"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  ml: { xs: 0.2, sm: 1 },
                  width: { xs: 30, sm: 38 },
                  height: { xs: 30, sm: 38 },
                  borderRadius: '12px',
                  background: '#FFFFFF',
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  p: 0.4,
                  border: isLight ? '1.5px solid rgba(43, 130, 144, 0.4)' : '2px solid rgba(76, 161, 175, 0.6)',
                  boxShadow: isLight ? '0 2px 8px rgba(0, 0, 0, 0.08)' : '0 0 12px rgba(76, 161, 175, 0.4)',
                  flexShrink: 0,
                  cursor: 'pointer',
                  textDecoration: 'none',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    transform: 'scale(1.08)',
                    boxShadow: isLight ? '0 4px 14px rgba(43, 130, 144, 0.3)' : '0 0 18px rgba(76, 161, 175, 0.7)',
                    borderColor: '#4CA1AF',
                  },
                }}
              >
                <img
                  src="https://glocalprayer.net/prayer/public/assets/img/gp-logo.png"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  alt="GPN Website"
                />
              </Box>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

          <Menu
            anchorEl={avatarMenuAnchor}
            open={isAvatarMenuOpen}
            onClose={handleAvatarMenuClose}
            PaperProps={{
              sx: {
                mt: 1.5,
                minWidth: 220,
                background: isLight ? '#FFFFFF' : 'rgba(30, 41, 59, 0.95)',
                backdropFilter: 'blur(10px)',
                border: isLight ? '1px solid rgba(0, 0, 0, 0.08)' : '1px solid rgba(255,255,255,0.1)',
                boxShadow: isLight ? '0 10px 30px rgba(0, 0, 0, 0.08)' : '0 10px 40px rgba(0, 0, 0, 0.5)',
                borderRadius: 2,
                color: isLight ? '#0F172A' : '#FFFFFF',
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: isLight ? '1px solid rgba(0,0,0,0.06)' : '1px solid rgba(255,255,255,0.1)' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: isLight ? '#0F172A' : '#FFFFFF' }}>
                {user?.name}
              </Typography>
              <Typography variant="caption" sx={{ color: isLight ? '#64748B' : 'rgba(255,255,255,0.6)' }}>
                {user?.email}
              </Typography>
            </Box>

            {/* Quick Theme Mode Toggle in Menu */}
            <MenuItem
              onClick={() => {
                toggleColorMode();
                handleAvatarMenuClose();
              }}
              sx={{
                color: isLight ? '#0F172A' : '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1.2,
                '&:hover': { background: isLight ? 'rgba(43, 130, 144, 0.08)' : 'rgba(76, 161, 175, 0.2)' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {isLight ? (
                  <DarkModeIcon fontSize="small" sx={{ color: '#6366F1' }} />
                ) : (
                  <LightModeIcon fontSize="small" sx={{ color: '#FBBF24' }} />
                )}
                <Typography variant="body2">{isLight ? 'Dark Mode' : 'Light Mode'}</Typography>
              </Box>
              <Chip
                label={isLight ? 'LIGHT' : 'DARK'}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  background: isLight ? 'rgba(43, 130, 144, 0.12)' : 'rgba(76, 161, 175, 0.2)',
                  color: isLight ? '#2B8290' : '#4CA1AF',
                }}
              />
            </MenuItem>
            <Divider sx={{ my: 0.5, borderColor: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.1)' }} />

            <MenuItem
              onClick={() => handleMenuItemClick('profile')}
              sx={{
                color: isLight ? '#0F172A' : '#FFFFFF',
                '&:hover': { background: isLight ? 'rgba(43, 130, 144, 0.08)' : 'rgba(76, 161, 175, 0.2)' },
              }}
            >
              My Profile
            </MenuItem>
            <Divider sx={{ my: 0.5, borderColor: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.1)' }} />

            <MenuItem
              onClick={() => handleMenuItemClick('change-password')}
              sx={{
                color: isLight ? '#0F172A' : '#FFFFFF',
                '&:hover': { background: isLight ? 'rgba(43, 130, 144, 0.08)' : 'rgba(76, 161, 175, 0.2)' },
              }}
            >
              Change Password
            </MenuItem>

            {isSwitchedOrgMode && (
              <>
                <Divider sx={{ my: 0.5, borderColor: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.1)' }} />
                <MenuItem
                  onClick={() => {
                    handleAvatarMenuClose();
                    handleExitOrgView();
                  }}
                  sx={{
                    color: '#EF4444',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1.2,
                    '&:hover': { background: 'rgba(239, 68, 68, 0.1)' },
                  }}
                >
                  <ExitToAppIcon fontSize="small" sx={{ color: '#EF4444' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Exit Organization View
                  </Typography>
                </MenuItem>
              </>
            )}

            {!isSwitchedOrgMode && (user?.role === 1 || myOrganizations.length > 1) && (
              <>
                <Divider sx={{ my: 0.5, borderColor: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.1)' }} />
                <MenuItem
                  onClick={() => handleMenuItemClick('switch-org')}
                  sx={{
                    color: isLight ? '#0D9488' : '#4CA1AF',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1.2,
                    '&:hover': { background: isLight ? 'rgba(43, 130, 144, 0.08)' : 'rgba(76, 161, 175, 0.2)' },
                  }}
                >
                  <SwapHorizIcon fontSize="small" sx={{ color: isLight ? '#0D9488' : '#4CA1AF' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Switch Organization
                  </Typography>
                </MenuItem>
              </>
            )}

                {/* View as Organization — Super Admin only */}
                {isSuperAdmin(user) && (
                  <>
                    <Divider sx={{ my: 0.5 }} />
                    <MenuItem
                      onClick={() => {
                        handleAvatarMenuClose();
                        fetchAllOrganizations();
                        setOpenOrgViewDialog(true);
                      }}
                      sx={{
                        color: '#a78bfa',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        py: 1.2,
                        '&:hover': { background: 'rgba(167, 139, 250, 0.15)' },
                      }}
                    >
                      <VisibilityIcon fontSize="small" sx={{ color: '#a78bfa' }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>View as Organization</Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.68rem' }}>Browse portal as an org admin</Typography>
                      </Box>
                    </MenuItem>
                  </>
                )}

            <Divider sx={{ my: 0.5 }} />


            <MenuItem
              onClick={() => handleMenuItemClick('logout')}
              sx={{
                py: 1.5,
                color: '#EF4444',
                '&:hover': {
                  background: 'rgba(239, 68, 68, 0.08)',
                },
              }}
            >
              Logout
            </MenuItem>
          </Menu>

          {/* About GPN & Portal Highlights Popover */}
          <Popover
            open={Boolean(aboutAnchor)}
            anchorEl={aboutAnchor}
            onClose={() => setAboutAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{
              sx: {
                p: 3,
                width: { xs: 300, sm: 380 },
                borderRadius: 3,
                background: isLight ? '#FFFFFF' : 'linear-gradient(135deg, rgba(30, 41, 59, 0.98) 0%, rgba(15, 23, 42, 0.98) 100%)',
                backdropFilter: 'blur(20px)',
                border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(76, 161, 175, 0.35)',
                boxShadow: isLight ? '0 20px 40px rgba(15, 23, 42, 0.15)' : '0 20px 40px rgba(0, 0, 0, 0.6)',
                color: isLight ? '#0F172A' : '#FFFFFF',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 2,
                  background: '#FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 0.4,
                  boxShadow: isLight ? '0 2px 8px rgba(15, 23, 42, 0.1)' : '0 2px 8px rgba(0,0,0,0.3)',
                  border: isLight ? '1px solid #E2E8F0' : 'none',
                }}
              >
                <img
                  src="https://glocalprayer.net/prayer/public/assets/img/gp-logo.png"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  alt="GPN Logo"
                />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: isLight ? '#0F172A' : '#FFFFFF', lineHeight: 1.2 }}>
                  Glocal Prayer Network
                </Typography>
                <Typography variant="caption" sx={{ color: isLight ? '#0D9488' : '#4CA1AF', fontWeight: 700 }}>
                  Global Christian Faith & Fellowship
                </Typography>
              </Box>
            </Box>

            <Typography variant="body2" sx={{ color: isLight ? '#334155' : 'rgba(255, 255, 255, 0.85)', fontSize: '0.85rem', lineHeight: 1.6, mb: 2 }}>
              At <strong>Glocal Prayer Network (GPN)</strong>, our mission is to foster spiritual growth and connection within the global Christian community. We provide tools for churches, Christian NGOs, fellowship groups, and families to deepen prayer lives, share testimonies, and support one another.
            </Typography>

            <Typography variant="caption" sx={{ color: isLight ? '#0D9488' : '#4CA1AF', fontWeight: 800, display: 'block', mb: 1, letterSpacing: '0.5px' }}>
              KEY CAPABILITIES OF THIS PORTAL:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, mb: 2.5 }}>
              <Typography variant="caption" sx={{ color: isLight ? '#334155' : 'rgba(255, 255, 255, 0.8)', fontSize: '0.82rem' }}>
                • <strong>Prayer Closet:</strong> Launch structured prayer initiatives with custom scriptures & timelines.
              </Typography>
              <Typography variant="caption" sx={{ color: isLight ? '#334155' : 'rgba(255, 255, 255, 0.8)', fontSize: '0.82rem' }}>
                • <strong>Prayers Hub:</strong> Moderate public requests & safeguard confidential intercessions.
              </Typography>
              <Typography variant="caption" sx={{ color: isLight ? '#334155' : 'rgba(255, 255, 255, 0.8)', fontSize: '0.82rem' }}>
                • <strong>Share & Accept:</strong> Connect givers and receivers in Christian generosity.
              </Typography>
              <Typography variant="caption" sx={{ color: isLight ? '#334155' : 'rgba(255, 255, 255, 0.8)', fontSize: '0.82rem' }}>
                • <strong>Ride Connect:</strong> Coordinate church transportation and volunteer drivers.
              </Typography>
              <Typography variant="caption" sx={{ color: isLight ? '#334155' : 'rgba(255, 255, 255, 0.8)', fontSize: '0.82rem' }}>
                • <strong>Worship & Devotions:</strong> Curate multilingual music, chord sheets, and daily reflections.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={() => {
                  setAboutAnchor(null);
                  navigate('/admin/guide');
                }}
                sx={{
                  color: isLight ? '#0D9488' : '#4CA1AF',
                  textTransform: 'none',
                  fontSize: '0.82rem',
                  fontWeight: 700,
                }}
              >
                Feature Guide →
              </Button>
              <Button
                size="small"
                variant="contained"
                endIcon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
                component="a"
                href="https://glocalprayer.net"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  background: 'linear-gradient(135deg, #4CA1AF 0%, #2C3E50 100%)',
                  color: '#FFFFFF',
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                }}
              >
                Visit Website
              </Button>
            </Box>
          </Popover>

      {/* Sidebar/Drawer */}
      <Box
        component="nav"
        data-no-translate="true"
        translate="no"
        className="notranslate"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="sidebar navigation"
      >
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: { xs: 'min(280px, 85vw)', sm: drawerWidth },
              background: isLight ? '#FFFFFF' : '#2C3E50',
              color: isLight ? '#0F172A' : '#FFFFFF',
            },
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop Drawer */}

        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              background: isLight ? '#FFFFFF' : '#2C3E50',
              color: isLight ? '#0F172A' : '#FFFFFF',
              borderRight: isLight ? '1px solid rgba(0, 0, 0, 0.08)' : 'none',
              boxShadow: isLight ? '2px 0 12px rgba(0, 0, 0, 0.03)' : '4px 0 6px rgba(44, 62, 80, 0.1)',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          minWidth: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'hidden',
          mt: 0,
          background: 'transparent',
          minHeight: '100vh',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 54, sm: 66 } }} />

        {/* Top Notice when Admin Agreement Pending */}
        {isAgreementPending && !isSwitchedOrgMode && (
          <Box
            sx={{
              width: '100%',
              px: { xs: 2, sm: 3 },
              py: { xs: 1.2, sm: 1.5 },
              background: isLight
                ? 'linear-gradient(90deg, #FEF3C7 0%, #FDE68A 100%)'
                : 'linear-gradient(90deg, rgba(245, 158, 11, 0.25) 0%, rgba(217, 119, 6, 0.20) 100%)',
              borderBottom: '2px solid #F59E0B',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
              boxShadow: '0 2px 8px rgba(245, 158, 11, 0.15)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: '1 1 320px', minWidth: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: isLight ? 'rgba(217, 119, 6, 0.15)' : 'rgba(245, 158, 11, 0.25)',
                  flexShrink: 0,
                }}
              >
                <WarningAmberIcon sx={{ color: isLight ? '#B45309' : '#FBBF24', fontSize: 22 }} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: isLight ? '#92400E' : '#FBBF24', fontSize: '0.85rem', letterSpacing: '0.4px' }}>
                    ACTION REQUIRED
                  </Typography>
                  <Chip
                    label="Agreement Pending"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      bgcolor: isLight ? '#FDE68A' : 'rgba(245, 158, 11, 0.3)',
                      color: isLight ? '#92400E' : '#FEF3C7',
                      border: '1px solid #F59E0B',
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: isLight ? '#78350F' : '#FEF3C7', fontSize: '0.85rem', lineHeight: 1.45, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  The <strong>GPN Administrator Agreement</strong> has not been completed. Please review and sign to ensure community stewardship, legal compliance, and uninterrupted portal access.
                </Typography>
              </Box>
            </Box>
            <Button
              size="medium"
              variant="contained"
              onClick={() => navigate('/admin/admin-agreement')}
              startIcon={<GavelIcon sx={{ fontSize: '18px !important' }} />}
              sx={{
                background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                color: '#FFFFFF',
                fontWeight: 700,
                fontSize: '0.82rem',
                textTransform: 'none',
                borderRadius: 2,
                px: 2.5,
                py: 0.8,
                flexShrink: 0,
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.35)',
                '&:hover': { background: 'linear-gradient(135deg, #D97706 0%, #B45309 100%)' },
              }}
            >
              Review and Sign Agreement
            </Button>
          </Box>
        )}

        <Box className="gpn-fade-in" sx={{ flex: 1, p: { xs: 1, sm: 2, md: 3 }, minWidth: 0, maxWidth: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
          <Outlet />
        </Box>

        {/* Persistent Admin Footer with Terms, Privacy & Agreement */}
        <AdminFooter />
      </Box>

      {/* Floating Support Chat Widget */}
      <SupportChatWidget />

      {/* Universal Multi-Language Form Translator for All Application Forms */}
      <UniversalFormTranslator />

      {/* Organization Details Dialog */}
      <Dialog
        open={openOrgDialog}
        onClose={handleOrgDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: isLight ? '#FFFFFF' : 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(255,255,255,0.1)',
            borderRadius: 3,
            color: isLight ? '#0F172A' : '#FFFFFF',
            boxShadow: isLight ? '0 20px 40px rgba(15, 23, 42, 0.16)' : '0 20px 50px rgba(0, 0, 0, 0.5)',
          },
        }}
      >
        <DialogTitle sx={{
          color: isLight ? '#0F172A' : '#FFFFFF',
          fontWeight: 800,
          borderBottom: isLight ? '1.5px solid #E2E8F0' : '1px solid rgba(255,255,255,0.1)',
          pb: 2,
        }}>
          Organization Details
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            {/* Large Logo */}
            <Box sx={{
              width: 200,
              height: 200,
              borderRadius: 3,
              background: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              boxShadow: isLight ? '0 4px 16px rgba(15, 23, 42, 0.1)' : '0 8px 24px rgba(76, 161, 175, 0.3)',
              border: isLight ? '1.5px solid #CBD5E1' : '2px solid rgba(76, 161, 175, 0.3)',
            }}>
              <img
                src={resolveOrgLogo(user?.role === 1 ? null : user?.organization_logo)}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = 'https://glocalprayer.net/prayer/public/assets/img/gp-logo.png';
                }}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                alt="Organization Logo"
              />
            </Box>

            {/* Organization Info */}
            <Box sx={{ width: '100%', textAlign: 'center' }}>
              <Typography variant="h5" sx={{ color: isLight ? '#0F172A' : '#FFFFFF', fontWeight: 800, mb: 1 }}>
                {user?.role === 1 ? 'Glocal Prayer Network' : user?.organization}
              </Typography>
              <Box sx={{ mt: 1.5, mb: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  endIcon={<OpenInNewIcon />}
                  component="a"
                  href="https://glocalprayer.net"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: isLight ? '#0D9488' : '#4CA1AF',
                    borderColor: isLight ? '#0D9488' : 'rgba(76, 161, 175, 0.4)',
                    borderWidth: '1.5px',
                    textTransform: 'none',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    borderRadius: 2,
                    background: isLight ? '#F0FDFA' : 'transparent',
                    '&:hover': {
                      background: isLight ? '#CCFBF1' : 'rgba(76, 161, 175, 0.15)',
                      borderColor: isLight ? '#0F766E' : '#6DD5FA',
                      color: isLight ? '#0F766E' : '#6DD5FA',
                    },
                  }}
                >
                  Visit GPN Website (glocalprayer.net)
                </Button>
              </Box>

              {/* {user?.role !== 1 && (
                <>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2 }}>
                    Organization Code: <strong style={{ color: '#4CA1AF' }}>{user?.organization_code || 'N/A'}</strong>
                  </Typography>

                  <Box sx={{
                    mt: 3,
                    p: 2,
                    background: 'rgba(76, 161, 175, 0.1)',
                    borderRadius: 2,
                    border: '1px solid rgba(76, 161, 175, 0.2)',
                  }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block', mb: 0.5 }}>
                      Admin
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                      {user?.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      {user?.email}
                    </Typography>
                  </Box>
                </>
              )} */}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={openLogoutDialog}
        onClose={() => setOpenLogoutDialog(false)}
        PaperProps={{
          sx: {
            background: isLight ? '#FFFFFF' : 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(255,255,255,0.1)',
            borderRadius: 3,
            p: 1,
            color: isLight ? '#0F172A' : '#FFFFFF',
            boxShadow: isLight ? '0 20px 40px rgba(15, 23, 42, 0.15)' : 'none',
          },
        }}
      >
        <DialogTitle sx={{ color: isLight ? '#0F172A' : '#FFFFFF', fontWeight: 800 }}>
          Confirm Logout
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: isLight ? '#334155' : 'rgba(255,255,255,0.8)', fontSize: '0.95rem' }}>
            Are you sure you want to log out?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, borderTop: isLight ? '1px solid #E2E8F0' : 'none', pt: isLight ? 1.5 : 0 }}>
          <Button
            onClick={() => setOpenLogoutDialog(false)}
            sx={{
              color: isLight ? '#475569' : 'rgba(255,255,255,0.6)',
              fontWeight: 600,
              '&:hover': { color: isLight ? '#0F172A' : '#FFFFFF', background: isLight ? '#F1F5F9' : 'transparent' }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLogoutConfirm}
            variant="contained"
            color="error"
            sx={{
              background: '#EF4444',
              color: '#FFFFFF',
              fontWeight: 700,
              boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
              '&:hover': { background: '#DC2626' }
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>

      {/* Switch Organization Dialog */}
      <Dialog
        open={openSwitchOrgDialog}
        onClose={() => setOpenSwitchOrgDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: isLight ? '#FFFFFF' : 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            backdropFilter: 'blur(20px)',
            border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(76, 161, 175, 0.3)',
            borderRadius: 3,
            p: 1,
            color: isLight ? '#0F172A' : '#FFFFFF',
            boxShadow: isLight ? '0 20px 40px rgba(15, 23, 42, 0.18)' : '0 20px 50px rgba(0, 0, 0, 0.5)',
          },
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: isLight ? '1.5px solid #E2E8F0' : '1px solid rgba(255,255,255,0.1)',
          pb: 2,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              background: isLight ? '#E6FFFA' : 'rgba(76, 161, 175, 0.2)',
              border: isLight ? '1.5px solid #99F6E4' : 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: isLight ? '#0D9488' : '#4CA1AF',
            }}>
              <SwapHorizIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: isLight ? '#0F172A' : '#FFFFFF', lineHeight: 1.2, fontSize: '1.25rem' }}>
                Switch Organization
              </Typography>
              <Typography variant="caption" sx={{ color: isLight ? '#334155' : 'rgba(255,255,255,0.7)', fontSize: '0.85rem', fontWeight: 500 }}>
                Select an organization to switch your active session
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2 }}>
          {switchOrgError && (
            <Box sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              background: 'rgba(239, 68, 68, 0.15)',
              border: '1px solid rgba(239, 68, 68, 0.4)',
              color: '#DC2626',
              fontSize: '0.85rem',
              fontWeight: 600,
            }}>
              {switchOrgError}
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            {myOrganizations.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: isLight ? '#475569' : 'rgba(255,255,255,0.5)' }}>
                <CircularProgress size={28} sx={{ color: isLight ? '#0D9488' : '#4CA1AF', mb: 1.5 }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Loading organizations...</Typography>
              </Box>
            ) : (
              myOrganizations.map((org) => {
                // Exactly one row should be active at a time.
                // Determine whether this org row represents the currently active organization:
                const activeId = effectiveOrgId || user?.org_id;
                const activeOrgName = (orgViewMode?.orgName || user?.organization || '').trim().toLowerCase();
                const thisOrgName = (org.org_name || '').trim().toLowerCase();

                // Check if this row is the active non-global org
                const isMatchingNonGlobal = org.id !== 'global' && (
                  Boolean(org.is_current) ||
                  (activeId && Number(org.id) === Number(activeId)) ||
                  (thisOrgName && activeOrgName && thisOrgName === activeOrgName)
                );

                // Global row is only active if NOT in switched mode AND no specific organization is active
                const isMatchingGlobal = org.id === 'global' && (
                  !isSwitchedOrgMode &&
                  (!activeId || Number(activeId) === 1 || activeOrgName === 'glocal prayer network' || !activeOrgName)
                );

                const isCurrent = isMatchingNonGlobal || isMatchingGlobal;

                // The currently active non-global org row should also offer an Exit action.
                const isActiveOrg = isCurrent && org.id !== 'global';
                const isSwitching = switchingOrgId === org.id;

                return (
                  <Box
                    key={org.id}
                    onClick={() => {
                      if (!isCurrent && !switchingOrgId) {
                        handleSwitchOrganization(org.id);
                      }
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 2,
                      borderRadius: 2.5,
                      background: isCurrent
                        ? (isLight ? '#F0FDFA' : 'linear-gradient(135deg, rgba(76, 161, 175, 0.22) 0%, rgba(44, 62, 80, 0.35) 100%)')
                        : (isLight ? '#F8FAFC' : 'rgba(255, 255, 255, 0.03)'),
                      border: isCurrent
                        ? (isLight ? '2px solid #0D9488' : '2px solid #4CA1AF')
                        : (isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.08)'),
                      boxShadow: isCurrent
                        ? (isLight ? '0 4px 14px rgba(13, 148, 136, 0.15)' : 'none')
                        : (isLight ? '0 1px 3px rgba(15, 23, 42, 0.04)' : 'none'),
                      cursor: isCurrent ? 'default' : 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': isCurrent ? {} : {
                        background: isLight ? '#F1F5F9' : 'rgba(76, 161, 175, 0.12)',
                        borderColor: isLight ? '#0D9488' : 'rgba(76, 161, 175, 0.4)',
                        transform: 'translateY(-2px)',
                        boxShadow: isLight ? '0 6px 18px rgba(15, 23, 42, 0.1)' : '0 4px 15px rgba(0,0,0,0.3)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        background: '#FFFFFF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        p: 0.5,
                        boxShadow: isLight ? '0 2px 8px rgba(15, 23, 42, 0.08)' : '0 2px 8px rgba(0,0,0,0.2)',
                        border: isLight ? '1px solid #E2E8F0' : 'none',
                        flexShrink: 0,
                      }}>
                        {org.logo ? (
                          <img
                            src={org.logo}
                            alt={org.org_name}
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                          />
                        ) : (
                          <BusinessIcon sx={{ color: isLight ? '#0D9488' : '#2C3E50', fontSize: 24 }} />
                        )}
                      </Box>

                      <Box>
                        <Typography sx={{ fontWeight: 700, color: isLight ? '#0F172A' : '#FFFFFF', fontSize: '1.05rem', lineHeight: 1.25 }}>
                          {org.org_name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Chip
                            label={getDisplayRoleName(org.role_name, org.role_id)}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.72rem',
                              fontWeight: 700,
                              background: isLight
                                ? (org.role_id === 1 ? '#FAF5FF' : org.role_id === 2 ? '#F0FDFA' : '#F1F5F9')
                                : (org.role_id === 1 ? 'rgba(168, 85, 247, 0.2)' : org.role_id === 2 ? 'rgba(76, 161, 175, 0.2)' : 'rgba(148, 163, 184, 0.2)'),
                              color: isLight
                                ? (org.role_id === 1 ? '#7E22CE' : org.role_id === 2 ? '#0F766E' : '#334155')
                                : (org.role_id === 1 ? '#C084FC' : org.role_id === 2 ? '#4CA1AF' : '#94A3B8'),
                              border: isLight
                                ? (org.role_id === 1 ? '1.5px solid #D8B4FE' : org.role_id === 2 ? '1.5px solid #99F6E4' : '1.5px solid #CBD5E1')
                                : '1px solid currentColor',
                            }}
                          />
                          {org.short_code && (
                            <Typography variant="caption" sx={{ color: isLight ? '#334155' : 'rgba(255,255,255,0.6)', fontSize: '0.8rem', fontWeight: 600 }}>
                              ({org.short_code})
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={{ ml: 2, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isCurrent && (
                        <Chip
                          icon={<CheckCircleIcon sx={{ fontSize: '16px !important', color: (isLight ? '#FFFFFF !important' : '#4CA1AF !important') }} />}
                          label="Active"
                          size="small"
                          sx={{
                            background: isLight ? '#0D9488' : 'rgba(76, 161, 175, 0.2)',
                            color: isLight ? '#FFFFFF !important' : '#4CA1AF',
                            fontWeight: 800,
                            fontSize: '0.8rem',
                            border: isLight ? 'none' : '1px solid #4CA1AF',
                            px: 0.5,
                          }}
                        />
                      )}
                      {isActiveOrg && isSwitchedOrgMode && (
                        /* Exit button visible only for Super Admin who switched into an org */
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ExitToAppIcon sx={{ fontSize: '15px !important' }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenSwitchOrgDialog(false);
                            handleExitOrgView();
                          }}
                          sx={{
                            color: '#DC2626',
                            borderColor: isLight ? '#FCA5A5' : 'rgba(239, 68, 68, 0.45)',
                            backgroundColor: isLight ? '#FEF2F2' : 'transparent',
                            textTransform: 'none',
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            py: 0.6,
                            px: 1.5,
                            borderRadius: '8px',
                            '&:hover': {
                              borderColor: '#DC2626',
                              background: '#FEE2E2',
                              color: '#B91C1C',
                            }
                          }}
                        >
                          Exit
                        </Button>
                      )}
                      {!isCurrent && (
                        isSwitching ? (
                          <CircularProgress size={22} sx={{ color: isLight ? '#0D9488' : '#4CA1AF' }} />
                        ) : (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSwitchOrganization(org.id);
                            }}
                            sx={{
                              backgroundColor: isLight ? '#0D9488' : 'rgba(76, 161, 175, 0.25)',
                              color: '#FFFFFF !important',
                              border: isLight ? 'none' : '1px solid #4CA1AF',
                              textTransform: 'none',
                              fontSize: '0.85rem',
                              fontWeight: 700,
                              py: 0.6,
                              px: 2,
                              borderRadius: '8px',
                              boxShadow: isLight ? '0 2px 6px rgba(13, 148, 136, 0.3)' : 'none',
                              '&:hover': {
                                backgroundColor: isLight ? '#0F766E' : 'rgba(76, 161, 175, 0.45)',
                                color: '#FFFFFF !important',
                                boxShadow: isLight ? '0 4px 12px rgba(13, 148, 136, 0.4)' : 'none',
                              }
                            }}
                          >
                            Switch
                          </Button>
                        )
                      )}
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, borderTop: isLight ? '1.5px solid #E2E8F0' : '1px solid rgba(255,255,255,0.08)' }}>
          <Button
            onClick={() => setOpenSwitchOrgDialog(false)}
            variant={isLight ? "outlined" : "text"}
            sx={{
              color: isLight ? '#0F172A' : 'rgba(255,255,255,0.85)',
              borderColor: isLight ? '#CBD5E1' : undefined,
              fontWeight: 700,
              fontSize: '0.88rem',
              textTransform: 'none',
              px: 2.5,
              py: 0.6,
              '&:hover': {
                color: isLight ? '#0D9488' : '#FFFFFF',
                borderColor: isLight ? '#0D9488' : undefined,
                background: isLight ? '#F0FDFA' : 'rgba(255,255,255,0.08)',
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ════════════════════════════════════════════════════════════
           View as Organization — Org Picker Dialog (Super Admin only)
         ════════════════════════════════════════════════════════════ */}
      <Dialog
        open={openOrgViewDialog}
        onClose={() => setOpenOrgViewDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: isLight ? '#FFFFFF' : 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            backdropFilter: 'blur(20px)',
            border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(167, 139, 250, 0.35)',
            borderRadius: 3,
            p: 1,
            color: isLight ? '#0F172A' : '#FFFFFF',
            boxShadow: isLight ? '0 20px 40px rgba(15, 23, 42, 0.15)' : 'none',
          },
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: isLight ? '1.5px solid #E2E8F0' : '1px solid rgba(255,255,255,0.1)',
          pb: 2,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 40, height: 40,
              borderRadius: '10px',
              background: isLight ? '#F5F3FF' : 'rgba(167, 139, 250, 0.2)',
              border: isLight ? '1.5px solid #DDD6FE' : 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: isLight ? '#7C3AED' : '#a78bfa',
            }}>
              <VisibilityIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: isLight ? '#0F172A' : '#FFFFFF', lineHeight: 1.2, fontSize: '1.25rem' }}>
                View as Organization
              </Typography>
              <Typography variant="caption" sx={{ color: isLight ? '#334155' : 'rgba(255,255,255,0.7)', fontSize: '0.85rem', fontWeight: 500 }}>
                Select an organization to see the portal from their perspective
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5, pb: 2 }}>
          {/* Info box */}
          <Box sx={{
            mb: 2, p: 1.5, borderRadius: 2,
            background: isLight ? '#F5F3FF' : 'rgba(167, 139, 250, 0.1)',
            border: isLight ? '1.5px solid #DDD6FE' : '1px solid rgba(167, 139, 250, 0.25)',
          }}>
            <Typography variant="caption" sx={{ color: isLight ? '#334155' : 'rgba(255,255,255,0.75)', lineHeight: 1.6, fontSize: '0.82rem' }}>
              <strong style={{ color: isLight ? '#7C3AED' : '#a78bfa' }}>ℹ Super Admin Only:</strong> You will temporarily see the portal
              as an Organization Admin would. Super Admin-only menus will be hidden.
              Your credentials are <strong>not changed</strong> — click <strong>Exit Org View</strong> to restore full access at any time.
            </Typography>
          </Box>

          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search organizations..."
            value={orgViewSearch}
            onChange={(e) => setOrgViewSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: isLight ? '#7C3AED' : 'rgba(255,255,255,0.4)', fontSize: 20 }} />
                </InputAdornment>
              ),
              sx: {
                color: isLight ? '#0F172A' : '#FFFFFF',
                background: isLight ? '#F8FAFC' : 'rgba(255,255,255,0.05)',
                borderRadius: 2,
                '& fieldset': { borderColor: isLight ? '#CBD5E1' : 'rgba(255,255,255,0.15)', borderWidth: '1.5px' },
                '&:hover fieldset': { borderColor: isLight ? '#7C3AED' : 'rgba(167, 139, 250, 0.4)' },
                '&.Mui-focused fieldset': { borderColor: isLight ? '#7C3AED' : '#a78bfa', borderWidth: '2px' },
              }
            }}
            sx={{ mb: 2 }}
          />

          {/* Org list */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 360, overflowY: 'auto',
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-thumb': { background: isLight ? '#CBD5E1' : 'rgba(167, 139, 250, 0.3)', borderRadius: 3 },
          }}>
            {loadingOrgs ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress size={28} sx={{ color: isLight ? '#7C3AED' : '#a78bfa' }} />
                <Typography variant="body2" sx={{ color: isLight ? '#334155' : 'rgba(255,255,255,0.5)', mt: 1.5, fontWeight: 600 }}>Loading organizations...</Typography>
              </Box>
            ) : (
              allOrganizations
                .filter(org => {
                  const name = (org.org_name || org.name || '').toLowerCase();
                  return name.includes(orgViewSearch.toLowerCase());
                })
                .map(org => (
                  <Box
                    key={org.id}
                    onClick={() => handleEnterOrgView(org)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 2,
                      p: 1.8, borderRadius: 2.5,
                      background: isLight ? '#F8FAFC' : 'rgba(255,255,255,0.03)',
                      border: isLight ? '1.5px solid #E2E8F0' : '1px solid rgba(255,255,255,0.08)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: isLight ? '#F1F5F9' : 'rgba(167, 139, 250, 0.12)',
                        borderColor: isLight ? '#7C3AED' : 'rgba(167, 139, 250, 0.4)',
                        transform: 'translateY(-1px)',
                        boxShadow: isLight ? '0 4px 12px rgba(15, 23, 42, 0.08)' : '0 4px 12px rgba(0,0,0,0.3)',
                      },
                    }}
                  >
                    <Box sx={{
                      width: 42, height: 42, borderRadius: 2,
                      background: '#FFFFFF',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      overflow: 'hidden', p: 0.5, flexShrink: 0,
                      boxShadow: isLight ? '0 2px 6px rgba(15, 23, 42, 0.08)' : '0 2px 8px rgba(0,0,0,0.2)',
                      border: isLight ? '1px solid #E2E8F0' : 'none',
                    }}>
                      {org.logo || org.organization_logo ? (
                        <img
                          src={org.logo || org.organization_logo}
                          alt={org.org_name || org.name}
                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                      ) : (
                        <BusinessIcon sx={{ color: isLight ? '#7C3AED' : '#2C3E50', fontSize: 24 }} />
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, color: isLight ? '#0F172A' : '#FFFFFF', fontSize: '0.98rem', lineHeight: 1.25 }}>
                        {org.org_name || org.name}
                      </Typography>
                      {org.short_code && (
                        <Typography variant="caption" sx={{ color: isLight ? '#334155' : 'rgba(255,255,255,0.6)', fontSize: '0.78rem', fontWeight: 600 }}>
                          ({org.short_code})
                        </Typography>
                      )}
                    </Box>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={(e) => { e.stopPropagation(); handleEnterOrgView(org); }}
                      sx={{
                        backgroundColor: isLight ? '#7C3AED' : 'rgba(167, 139, 250, 0.25)',
                        color: '#FFFFFF !important',
                        border: isLight ? 'none' : '1px solid rgba(167, 139, 250, 0.4)',
                        textTransform: 'none',
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        py: 0.5, px: 1.5,
                        borderRadius: '8px',
                        flexShrink: 0,
                        boxShadow: isLight ? '0 2px 6px rgba(124, 58, 237, 0.3)' : 'none',
                        '&:hover': {
                          backgroundColor: isLight ? '#6D28D9' : 'rgba(167, 139, 250, 0.45)',
                          color: '#FFFFFF !important',
                        }
                      }}
                    >
                      View
                    </Button>
                  </Box>
                ))
            )}
            {!loadingOrgs && allOrganizations.filter(org => {
              const name = (org.org_name || org.name || '').toLowerCase();
              return name.includes(orgViewSearch.toLowerCase());
            }).length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4, color: isLight ? '#475569' : 'rgba(255,255,255,0.4)' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>No organizations found.</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, borderTop: isLight ? '1.5px solid #E2E8F0' : '1px solid rgba(255,255,255,0.08)' }}>
          <Button
            onClick={() => setOpenOrgViewDialog(false)}
            variant={isLight ? "outlined" : "text"}
            sx={{
              color: isLight ? '#0F172A' : 'rgba(255,255,255,0.85)',
              borderColor: isLight ? '#CBD5E1' : undefined,
              fontWeight: 700,
              fontSize: '0.88rem',
              textTransform: 'none',
              px: 2.5,
              py: 0.6,
              '&:hover': {
                color: isLight ? '#7C3AED' : '#FFFFFF',
                borderColor: isLight ? '#7C3AED' : undefined,
                background: isLight ? '#F5F3FF' : 'rgba(255,255,255,0.08)',
              }
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminLayout;
